/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import type {AddChange, CommitChange, Diff, RemoveChange} from './diffTypes';

import joinPath from '../joinPath';

/**
 * Post-processes a Diff to detect renames. Uses two passes:
 *
 * 1. **Exact OID match** — pairs of remove+add with the same blob OID (R100).
 * 2. **API-guided exact path match** — uses the rename map from GitHub's REST
 *    API (which includes `previous_filename`) to directly pair add+remove
 *    entries by their exact old and new paths. This uses Git's server-side
 *    rename detection — no client-side heuristics needed.
 *
 * @param diff Raw diff from tree walk
 * @param renameMap Map of newPath → oldPath from GitHub's REST API for PR files.
 *   When empty/undefined, only exact OID matching is performed (e.g. for commit
 *   views where we don't have PR file metadata).
 */
export function detectRenames(diff: Diff, renameMap?: ReadonlyMap<string, string>): Diff {
  // Separate removes and adds; keep modifies/renames as-is.
  const removes: RemoveChange[] = [];
  const adds: AddChange[] = [];
  const others: CommitChange[] = [];

  for (const change of diff) {
    switch (change.type) {
      case 'remove':
        removes.push(change);
        break;
      case 'add':
        adds.push(change);
        break;
      default:
        others.push(change);
        break;
    }
  }

  // Nothing to match.
  if (removes.length === 0 || adds.length === 0) {
    return diff;
  }

  const result: CommitChange[] = [...others];
  const matchedRemoves = new Set<RemoveChange>();
  const matchedAdds = new Set<AddChange>();

  // ── Pass 1: exact OID match (R100) ──────────────────────────────────
  const addsByOid = new Map<string, AddChange[]>();
  for (const add of adds) {
    const list = addsByOid.get(add.entry.oid);
    if (list != null) {
      list.push(add);
    } else {
      addsByOid.set(add.entry.oid, [add]);
    }
  }

  for (const remove of removes) {
    const candidates = addsByOid.get(remove.entry.oid);
    if (candidates != null && candidates.length > 0) {
      const add = candidates.shift()!;
      const oldPath = joinPath(remove.basePath, remove.entry.name);
      const newPath = joinPath(add.basePath, add.entry.name);

      if (oldPath !== newPath) {
        matchedRemoves.add(remove);
        matchedAdds.add(add);
        result.push({
          type: 'rename',
          oldBasePath: remove.basePath,
          newBasePath: add.basePath,
          before: remove.entry,
          after: add.entry,
        });
      }

      if (candidates.length === 0) {
        addsByOid.delete(remove.entry.oid);
      }
    }
  }

  // ── Pass 2: API-guided exact path match ──────────────────────────────
  // GitHub already ran Git's rename detection server-side. The renameMap
  // provides newPath → oldPath mappings from the REST API's
  // `previous_filename` field. We use exact paths to pair entries — no
  // heuristics needed.
  if (renameMap != null && renameMap.size > 0) {
    // Index unmatched removes by their full path for O(1) lookup.
    const removesByPath = new Map<string, RemoveChange>();
    for (const remove of removes) {
      if (!matchedRemoves.has(remove)) {
        removesByPath.set(joinPath(remove.basePath, remove.entry.name), remove);
      }
    }

    for (const add of adds) {
      if (matchedAdds.has(add)) {
        continue;
      }

      const newPath = joinPath(add.basePath, add.entry.name);
      const oldPath = renameMap.get(newPath);
      if (oldPath == null) {
        continue;
      }

      const remove = removesByPath.get(oldPath);
      if (remove == null) {
        continue;
      }

      matchedRemoves.add(remove);
      matchedAdds.add(add);
      removesByPath.delete(oldPath);
      result.push({
        type: 'rename',
        oldBasePath: remove.basePath,
        newBasePath: add.basePath,
        before: remove.entry,
        after: add.entry,
      });
    }
  }

  // Add remaining unmatched removes and adds.
  for (const remove of removes) {
    if (!matchedRemoves.has(remove)) {
      result.push(remove);
    }
  }
  for (const add of adds) {
    if (!matchedAdds.has(add)) {
      result.push(add);
    }
  }

  // Re-sort by path to maintain depth-first order.
  result.sort((a, b) => {
    const pathA = getPathForSort(a);
    const pathB = getPathForSort(b);
    return pathA.localeCompare(pathB);
  });

  return result;
}

function getPathForSort(change: CommitChange): string {
  switch (change.type) {
    case 'add':
      return joinPath(change.basePath, change.entry.name);
    case 'remove':
      return joinPath(change.basePath, change.entry.name);
    case 'modify':
      return joinPath(change.basePath, change.before.name);
    case 'rename':
      return joinPath(change.newBasePath, change.after.name);
  }
}
